import React, { useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  /** Long, wrapping text (e.g. the full sentence behind a truncated row).
   *  Rendered in a portal with fixed positioning so a scrolling or
   *  overflow-hidden ancestor can never clip it. Opens below the trigger,
   *  or above it when side="top" is passed explicitly. */
  multiline?: boolean;
  /** Classes for the wrapper — e.g. "flex w-full" to wrap a full-width row. */
  wrapperClassName?: string;
}

const positionClasses = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
};

/** Max tooltip width in multiline mode (px) — matches max-w-[22rem] below. */
const MULTILINE_MAX_W = 352;

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  side,
  className,
  multiline = false,
  wrapperClassName,
}) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; above: boolean } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const id = useId();

  const show = () => {
    if (multiline && wrapperRef.current) {
      const r = wrapperRef.current.getBoundingClientRect();
      const left = Math.max(8, Math.min(r.left + 16, window.innerWidth - MULTILINE_MAX_W - 8));
      const above = side === 'top';
      setPos({ top: above ? r.top - 4 : r.bottom + 4, left, above });
    }
    setVisible(true);
  };
  const hide = () => setVisible(false);

  return (
    <div
      ref={wrapperRef}
      className={cn('relative inline-flex', wrapperClassName)}
      onMouseEnter={show}
      onMouseLeave={hide}
      // Focus events bubble in React, so a focusable child (a row button)
      // shows the tooltip for keyboard users too.
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {visible && !multiline && (
        <div
          className={cn(
            'absolute z-50 px-2.5 py-1.5 text-xs font-medium text-white bg-navy-900 dark:bg-navy-700 rounded-lg shadow-lg whitespace-nowrap pointer-events-none',
            'animate-fade-in',
            positionClasses[side ?? 'top'],
            className
          )}
          role="tooltip"
        >
          {content}
        </div>
      )}
      {visible &&
        multiline &&
        pos &&
        createPortal(
          <div
            id={id}
            role="tooltip"
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              transform: pos.above ? 'translateY(-100%)' : undefined,
            }}
            className={cn(
              'z-[100] max-w-[22rem] px-3 py-2 text-[12.5px] leading-relaxed text-white bg-navy-900 dark:bg-navy-700 rounded-lg shadow-lg whitespace-normal pointer-events-none',
              'animate-fade-in',
              className
            )}
          >
            {content}
          </div>,
          document.body
        )}
    </div>
  );
};

export default Tooltip;
